import React, { useContext } from "react";
import { Form, Alert } from "react-bootstrap";
import { useSyncedField } from "../../components/Sidebar";
import SidebarContext from "../../components/Sidebar/SidebarContext";
import IntegrationExecutionHistory from "./IntegrationExecutionHistory";
import IntegrationCollapsible from "./IntegrationCollapsible";

/**
 * Renders multi-line template copy (setup_instructions / auth.setup_instructions)
 * as a simple numbered/plain list — the catalog stores this as plain text
 * with one step per line, no markdown parser needed.
 */
function InstructionBlock({ title, text }) {
    if (!text) return null;
    const lines = text.split("\n").map((l) => l.trim()).filter(Boolean);

    return (
        <div className="mb-4">
            <h6 className="text-uppercase text-muted small mb-2">{title}</h6>
            <StepsList lines={lines} />
        </div>
    );
}

/** Numbered-badge step list — used both standalone (InstructionBlock) and inside the yellow setup note. */
function StepsList({ lines }) {
    if (lines.length === 0) return null;
    return (
        <div className="d-flex flex-column gap-2">
            {lines.map((line, i) => (
                <div key={i} className="d-flex align-items-center gap-2">
                    <span className="step-badge d-flex align-items-center justify-content-center flex-shrink-0 fw-semibold">
                        {i + 1}
                    </span>
                    <span className="font-size-14px">{line}</span>
                </div>
            ))}
        </div>
    );
}

/**
 * First tab of the integration setup wizard. Names the integration, then
 * walks the user through whatever manual steps the external system requires
 * (create an app, enable a webhook, register OAuth credentials, ...) before
 * they touch Authentication/Connection. The shared SidebarView footer's
 * "Next" button (see SidebarView.jsx) is what moves them forward — no
 * separate button is added here so this stays consistent with every other
 * multi-page sidebar in the app.
 */
const IntegrationBeforeYouBegin = ({ template }) => {
    const { entity } = useContext(SidebarContext);
    const [name, setName] = useSyncedField("name", template?.name ?? "");
    const [description, setDescription] = useSyncedField("description", template?.description ?? "");
    const [is_active, setIsActive] = useSyncedField("is_active", true);

    const needsAuthSetup = !!template?.auth?.setup_instructions;
    const setupLines = (template?.setup_instructions || "")
        .split("\n")
        .map((l) => l.trim())
        .filter(Boolean);
    const showSetupNote = !!(template?.docs_url || setupLines.length > 0);

    return (
        <div className="p-3">
            {template && (
                <div className="mb-4 pb-3 border-bottom">
                    <div className="d-flex align-items-center gap-2 mb-2">
                        {template.icon && (
                            <img
                                src={template.icon}
                                alt=""
                                className="w-32px h-32px object-fit-contain"
                                onError={(e) => { e.target.style.display = "none"; }}
                            />
                        )}
                        <div>
                            <div className="fw-semibold">{template.name}</div>
                            {template.category && (
                                <div className="text-muted small">{template.category}</div>
                            )}
                        </div>
                    </div>

                    {showSetupNote && (
                        <Alert variant="warning" className="py-3 px-3 mb-0 small">
                            <div className="d-flex align-items-start gap-2 mb-1">
                                <i className="fa fa-exclamation-circle mt-1" />
                                <div>
                                    {template.docs_url ? (
                                        <>
                                            Please complete the essential setup steps within{" "}
                                            <Alert.Link href={template.docs_url} target="_blank" rel="noreferrer">
                                                {template.name}
                                            </Alert.Link>{" "}
                                            to enable this integration — this is required for it to work correctly.
                                        </>
                                    ) : (
                                        <>Please complete the essential setup steps below to enable this integration.</>
                                    )}
                                </div>
                            </div>

                            {setupLines.length > 0 && (
                                <div className="ms-4 my-3">
                                    <StepsList lines={setupLines} />
                                </div>
                            )}

                            <div className="pt-2 border-top border-color-black-10">
                                Once you've completed these steps, click <strong>Next</strong> to continue.
                                {" "}Stuck? <Alert.Link href="/support">Contact Support</Alert.Link>.
                            </div>
                        </Alert>
                    )}
                </div>
            )}

            <Form className="mb-4">
                <Form.Group className="mb-3">
                    <Form.Label>
                        Name <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder={template?.name || "My integration"}
                    />
                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Label>Description</Form.Label>
                    <Form.Control
                        as="textarea"
                        rows={2}
                        value={description ?? ""}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder="What is this integration for?"
                    />
                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Check
                        type="switch"
                        id="integration-active-switch"
                        label="Active"
                        checked={!!is_active}
                        onChange={(e) => setIsActive(e.target.checked)}
                    />
                </Form.Group>
            </Form>

            {needsAuthSetup && (
                <InstructionBlock title="Setting Up Authentication" text={template.auth.setup_instructions} />
            )}

            {entity?.id && (
                <IntegrationCollapsible title="Execution History" bodyClassName="p-0">
                    <IntegrationExecutionHistory />
                </IntegrationCollapsible>
            )}
        </div>
    );
};

export default IntegrationBeforeYouBegin;
